import { get } from "lodash-es";
import { usePostHog } from "posthog-js/react";
import React, { FC, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Tabs from "@mui/material/Tabs";

import {
  captureProductEvent,
  productAnalyticsEvents,
} from "../../analytics/events";
import BeforeBackActionDialog from "../../components/BeforeBackActionDialog";
import NamespaceRoleGuard from "../../components/NamespaceRoleGuard";
import StickyEditBar from "../../components/StickyEditBar";
import {
  CertificateIcon,
  ClockIcon,
  HeadersIcon,
  LoadBalancerIcon,
  MCPIcon,
  MoveIcon,
  OutlinedSettingsIcon,
} from "../../components/icons";
import Tab from "../../components/styled/Tab";
import useReadOnly from "../../hooks/use-readonly";
import {
  clearForm,
  getRouteFormProperty,
  setFormRoute,
} from "../../slices/routes";
import { useDispatch, useSelector } from "../../store";
import {
  Route,
  useGetRouteCertificatesQuery,
  useGetRouteQuery,
  useListDefaultTemplatesQuery,
} from "../../store/pomeriumApi";
import { Permission, getPermission } from "../../types/permission";
import { RouteType, protocolToRouteType } from "../../utils/route-type";
import { EditWrapper } from "./EditWrapper";
import { AppliedPolicies } from "./components/AppliedPolicies";
import { Advanced } from "./components/edit_sections/Advanced";
import { General } from "./components/edit_sections/General";
import { Headers } from "./components/edit_sections/Headers";
import MCP from "./components/edit_sections/MCP";
import { Matchers } from "./components/edit_sections/Matchers";
import { TLS } from "./components/edit_sections/TLS";
import { Timeouts } from "./components/edit_sections/TimeOuts";
import { useRouteFormFunctions } from "./useRouteFormFunctions";
import { isDeepEmpty } from "@/utils/obj";

const tabOptionsBase = [
  { value: "main", icon: <OutlinedSettingsIcon />, label: "General" },
  { value: "headers", icon: <HeadersIcon />, label: "Headers" },
  { value: "tls", icon: <CertificateIcon />, label: "TLS" },
  { value: "timeouts", icon: <ClockIcon />, label: "Timeouts" },
  { value: "advanced", icon: <LoadBalancerIcon />, label: "Advanced" },
];

const Edit: FC<React.PropsWithChildren> = () => {
  const posthog = usePostHog();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [showBackWarning, setShowBackWarning] = useState(false);

  const { organizationId, clusterId } = useSelector((state) => state.current);
  const { routeId } = useParams();
  const id = useSelector(getRouteFormProperty("id", "")) as string;
  const from = useSelector(getRouteFormProperty("from", "")) as string;
  const mcp = useSelector(getRouteFormProperty("mcp", null)) as {
    client?: unknown;
    server?: unknown;
  } | null;
  const currentProtocol = from.includes("://")
    ? from.substring(0, from.indexOf("://") + 3)
    : "https://";
  const routeType = useSelector(
    getRouteFormProperty(
      "routeType",
      protocolToRouteType(currentProtocol, mcp),
    ),
  ) as RouteType;
  const isMCPRoute = routeType === "MCP";
  const { shouldSave, handleClickBack, handleSubmit } = useRouteFormFunctions(
    routeId,
    setShowBackWarning,
  );

  const formErrors = useSelector((state) => state.routes.formErrors);
  const { data: route, isLoading } = useGetRouteQuery(
    {
      organizationId,
      routeId: routeId || "",
    },
    { skip: !organizationId || !routeId },
  );

  const { data: defaultRoute, isLoading: defaultLoading } =
    useListDefaultTemplatesQuery("route", { skip: !!routeId });

  const { data: matchingCertificates } = useGetRouteCertificatesQuery(
    {
      organizationId,
      routeId: routeId || "",
    },
    { skip: !organizationId || !routeId },
  );

  const readonly = useReadOnly({ clusterId, organizationId });
  const [tabValue, setTabValue] = React.useState(
    searchParams.get("tab") === "appliedPolicies" ? "policies" : "main",
  );
  const [tabAnnouncement, setTabAnnouncement] = useState("");
  const prevIsMCPRouteRef = useRef(isMCPRoute);

  const handleChange = (
    _event: any,
    newValue: React.SetStateAction<string>,
  ) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (routeId && route) {
      dispatch(setFormRoute(route));
      if (["SSH", "Verify"].indexOf(route?.name) > -1) {
        posthog.capture("clicked_canned_route_" + route?.name?.toLowerCase());
      }
      posthog.capture("route_edited");
    } else {
      captureProductEvent(productAnalyticsEvents.routeCreateStarted);
      posthog.capture("new_route_started");
    }
  }, [routeId, isLoading]);

  useEffect(() => {
    if (!routeId && defaultRoute && !searchParams.get("routePrepopulated")) {
      dispatch(
        setFormRoute(
          get(defaultRoute, "0.defaultProperties", {}) as Partial<Route>,
        ),
      );
    }
  }, [routeId, defaultLoading, searchParams.get("routePrepopulated")]);

  useEffect((): (() => void) => {
    return (): void => {
      dispatch(clearForm());
    };
  }, []);

  useEffect(() => {
    if (prevIsMCPRouteRef.current === isMCPRoute) {
      return;
    }
    prevIsMCPRouteRef.current = isMCPRoute;
    setTabAnnouncement(
      isMCPRoute
        ? "MCP settings tab added to route navigation"
        : "MCP settings tab removed from route navigation",
    );
  }, [isMCPRoute]);

  useEffect(() => {
    if (!isMCPRoute && tabValue === "mcp") {
      // If focus was on the MCP tab as it unmounts, restore focus to the
      // General tab so keyboard navigation doesn't drop back to <body>.
      const mcpTabFocused = document.activeElement?.id === "tab-mcp";
      setTabValue("main");
      if (mcpTabFocused) {
        requestAnimationFrame(() => {
          document.getElementById("tab-main")?.focus();
        });
      }
    }
  }, [isMCPRoute, tabValue]);

  if (isLoading) {
    return <></>;
  }

  if (routeId && !id) {
    return (
      <Box display="flex" sx={{ m: 10 }}>
        <Alert severity="warning">
          The route does not exist and may have been deleted!
        </Alert>
      </Box>
    );
  }

  // Add Path Matching tab if Admin
  const canEditMatchers =
    getPermission(Permission.Admin /*currentNamespace?.permission */) ===
    Permission.Admin;
  const tabOptions = tabOptionsBase.slice();
  if (canEditMatchers) {
    tabOptions.splice(1, 0, {
      value: "matchers",
      icon: <MoveIcon />,
      label: "Path Matching",
    });
  }
  if (isMCPRoute) {
    // Insert the MCP tab just before Advanced (which is always last).
    tabOptions.splice(tabOptions.length - 1, 0, {
      value: "mcp",
      icon: <MCPIcon />,
      label: "MCP",
    });
  }
  const safeTabValue = tabOptions.some((t) => t.value === tabValue)
    ? tabValue
    : "main";

  return (
    <EditWrapper routeId={routeId}>
      <BeforeBackActionDialog
        open={showBackWarning}
        onClose={() => setShowBackWarning(false)}
        navPath="/app/management/routes"
      />
      <NamespaceRoleGuard namespaceId={clusterId} role={"viewer"} warn={true}>
        <div role="status" aria-live="polite" className="sr-only">
          {tabAnnouncement}
        </div>
        <form autoComplete="off" onSubmit={handleSubmit}>
          <Box sx={{ display: { xs: "block", md: "none" }, mb: 2 }}>
            <Select
              value={safeTabValue}
              onChange={(e) => setTabValue(e.target.value)}
              fullWidth
              sx={{ bgcolor: "background.paper" }}
              renderValue={(selected) => {
                const selectedTab = tabOptions.find(
                  (t) => t.value === selected,
                );
                if (!selectedTab) return null;
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {selectedTab.icon}
                    {selectedTab.label}
                  </Box>
                );
              }}
              MenuProps={{
                disableScrollLock: true,
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              {tabOptions.map((t) => (
                <MenuItem
                  key={t.value}
                  value={t.value}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {t.icon}
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Grid container spacing={3}>
            <Grid
              sx={{ display: { xs: "none", md: "block" } }}
              size={{
                xs: 12,
                md: "auto",
              }}
            >
              <Box style={{ position: "sticky", top: 0 }}>
                <Tabs
                  aria-labelledby={"routeEditVerticalFormSectionTabs"}
                  orientation="vertical"
                  onChange={handleChange}
                  indicatorColor="primary"
                  textColor="primary"
                  selectionFollowsFocus={true}
                  value={safeTabValue}
                  variant="fullWidth"
                >
                  {tabOptions.map((t) => (
                    <Tab
                      key={t.value}
                      label={t.label}
                      icon={React.cloneElement(t.icon, {
                        color: "inherit",
                        sx: { pr: 0.5 },
                      })}
                      iconPosition="start"
                      aria-controls={`tabpanel-${t.value}`}
                      id={`tab-${t.value}`}
                      disableFocusRipple={true}
                      disableRipple={true}
                      value={t.value}
                    />
                  ))}
                </Tabs>
              </Box>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: "grow",
              }}
            >
              <Container
                component={Paper}
                maxWidth={false}
                disableGutters={true}
              >
                <General
                  readonly={readonly}
                  tabValue={safeTabValue}
                  route={route}
                />
                {canEditMatchers && (
                  <Matchers readonly={readonly} tabValue={safeTabValue} />
                )}
                <Headers readonly={readonly} tabValue={safeTabValue} />
                {!!routeId && route && (
                  <AppliedPolicies
                    readonly={readonly}
                    route={route || {}}
                    tabValue={safeTabValue}
                  />
                )}
                {isMCPRoute && (
                  <MCP readonly={readonly} tabValue={safeTabValue} />
                )}
                <TLS
                  readonly={readonly}
                  tabValue={safeTabValue}
                  routeId={routeId}
                  matchingCertificates={matchingCertificates || []}
                />
                <Timeouts readonly={readonly} tabValue={safeTabValue} />
                <Advanced readonly={readonly} tabValue={safeTabValue} />
              </Container>
            </Grid>
          </Grid>
          <StickyEditBar
            name="Route"
            allowCancel={true}
            allowSave={shouldSave() && isDeepEmpty(Object(formErrors))}
            cancelType={"Back"}
            onCancel={(): void => handleClickBack()}
            onSave={handleSubmit}
          />
        </form>
      </NamespaceRoleGuard>
    </EditWrapper>
  );
};

export default Edit;
